import { useState, useCallback, useRef } from 'react';
import type { OrderStatusUpdatesResponse } from '@lds-efsp/shared-contracts';
import { useApi, API_BASE_URL } from '../../../api/useApi';

// In-memory cache with TTL
interface CacheEntry {
  data: OrderStatusUpdatesResponse;
  timestamp: number;
}

const cache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 60 * 1000; // 60 seconds

/**
 * Hook for lazy-loading order status updates
 *
 * The portal's one reader of `GET /api/orders/{id}/status-updates`: the order
 * detail page's Status Timeline and every status-preview popover render from
 * this hook, so a durable row reaches all of them or none. Each caller issues
 * the read itself — the hook is lazy, never fetching on mount.
 *
 * Features:
 * - Lazy loading: Only fetches when fetchUpdates is called
 * - In-memory cache, shared per order id, with 60s TTL
 * - Loading state management
 * - Error handling
 *
 * @param orderId - The order ID to fetch status updates for
 */
export function useOrderStatusUpdates(orderId: string) {
  const { get } = useApi();
  const [data, setData] = useState<OrderStatusUpdatesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchInProgressRef = useRef(false);

  const fetchUpdates = useCallback(async () => {
    // An order-less read has nothing to reach: the empty id renders as the
    // malformed `/api/orders//status-updates`. Both call sites guard their own
    // read (the page only reads once it has an id), and this is the backstop
    // for the one that forgets.
    if (!orderId) {
      return;
    }

    // Check if already fetching
    if (fetchInProgressRef.current) {
      return;
    }

    // Check cache first
    const cached = cache.get(orderId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      setData(cached.data);
      return;
    }

    fetchInProgressRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const response = await get<OrderStatusUpdatesResponse>(
        `${API_BASE_URL}/api/orders/${orderId}/status-updates`,
      );

      // Update cache
      cache.set(orderId, {
        data: response,
        timestamp: Date.now(),
      });

      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch status updates'));
      setData(null);
    } finally {
      setIsLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [orderId, get]);

  const invalidateCache = useCallback(() => {
    cache.delete(orderId);
  }, [orderId]);

  const refetch = useCallback(async () => {
    invalidateCache();
    await fetchUpdates();
  }, [invalidateCache, fetchUpdates]);

  return {
    data,
    isLoading,
    error,
    fetchUpdates,
    refetch,
    invalidateCache,
  };
}

